import { useState, useCallback, useRef, useEffect } from "preact/hooks";
import { marked } from "marked";
import type { Action } from "../state/actions.ts";
import type { TelegramDocument } from "../types/document.ts";
import { applyTool } from "../assistant/apply-tool.ts";
import { authHeader, getToken } from "../auth.ts";

export type ChatMessage = { role: "user" | "assistant"; content: string };

export function useAssistantChat(
  dispatch: (action: Action) => void,
  getDocument: () => TelegramDocument,
  enabled: boolean,
) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled || !getToken()) return;
    fetch("/api/chat/history", { headers: authHeader() })
      .then((r) => r.json())
      .then((data: ChatMessage[]) => {
        if (Array.isArray(data))
          setMessages(
            data.map((m) =>
              m.role === "assistant" ? { ...m, content: marked.parse(m.content) as string } : m,
            ),
          );
      })
      .catch(() => {});
  }, [enabled]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!enabled || !getToken() || isStreaming || !text.trim()) return;

      setMessages((prev) => [...prev, { role: "user", content: text }]);
      setIsStreaming(true);
      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeader() },
          body: JSON.stringify({
            message: text,
            currentDocument: getDocument(),
          }),
          signal: controller.signal,
        });

        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let assistantMarkdown = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          let eventType = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7);
            } else if (line.startsWith("data: ")) {
              const data = JSON.parse(line.slice(6));
              if (eventType === "text") {
                assistantMarkdown += data.delta;
                const html = marked.parse(assistantMarkdown) as string;
                setMessages((prev) => {
                  const last = prev[prev.length - 1];
                  return [...prev.slice(0, -1), { ...last, content: html }];
                });
              } else if (eventType === "tool") {
                const actions = applyTool(data);
                for (const action of actions) dispatch(action);
              }
            }
          }
        }
      } catch (e) {
        if ((e as Error).name !== "AbortError") {
          setMessages((prev) => [
            ...prev.slice(0, -1),
            {
              role: "assistant",
              content: marked.parse("Something went wrong. Please try again.") as string,
            },
          ]);
        }
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [dispatch, enabled, isStreaming, getDocument],
  );

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  return { messages, isStreaming, sendMessage, cancel };
}
